import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { KristianiaLogo } from '../KristianiaLogo/KristianiaLogo';
import { Link } from '../Link/Link';
import { LinkType } from '../Link/LinkType.csharp';
import { LinkVariant } from '../Link/LinkVariant.csharp';
import { List } from '../List/List';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { FooterVariant } from '~/Blocks/Footer/FooterVariant.csharp';
import { useLogoSettings } from '~/Features/Configuration/Hooks/useLogoSettings';
import type { FooterProps } from './FooterProps.csharp';
import styles from './Styles/Footer.module.css';

export const Footer: React.FC<React.PropsWithChildren<FooterProps>> = props => {
    const { variant, sections, socialLinks, translations, copyright, startPageUrl } = props;

    const getLinkVariant = () => (variant === FooterVariant.Default ? LinkVariant.LightBlack : LinkVariant.Primary);

    const logoSettings = useLogoSettings();

    return (
        <footer className={classNames(styles.Footer, styles[`Variant--${variant}`])}>
            <div className={styles.Inner}>
                <Link type={LinkType.Clear} href={startPageUrl} className={styles.Logo}>
                    <KristianiaLogo
                        label={logoSettings.defaultAltText}
                        type="fixed"
                        version="horizontal"
                        color="white"
                        size="large"
                    />
                </Link>
                <div className={styles.Sections}>
                    <List
                        className={styles.LinksSections}
                        items={sections}
                        renderItem={section =>
                            section.items &&
                            section.items.length > 0 && (
                                <li className={styles.LinksSection} key={uniqueId('Footer__linksSection')}>
                                    <h2 className={styles.Heading}>{section.heading}</h2>
                                    <List
                                        className={styles.LinksList}
                                        items={section.items}
                                        renderItem={item => (
                                            <li key={uniqueId('Footer__linksListItem')}>
                                                <Link
                                                    href={item.linkUrl}
                                                    label={item.title}
                                                    variant={getLinkVariant()}
                                                    className={styles.LinksListItem}
                                                />
                                            </li>
                                        )}
                                    />
                                </li>
                            )
                        }
                    />
                    {socialLinks.links && socialLinks.links.length > 0 && (
                        <div className={styles.SocialLinksSection}>
                            <h2 className={styles.Heading}>{socialLinks.heading ?? translations.socialMediaHeading}</h2>
                            <List
                                className={styles.SocialLinksList}
                                items={socialLinks.links}
                                renderItem={item => (
                                    <li key={uniqueId('Footer__socialLinksListItem')}>
                                        <Link
                                            {...item.link}
                                            external
                                            showIsExternal={false}
                                            type={LinkType.Clear}
                                            className={styles.SocialLinksListItem}
                                        >
                                            <SvgIcon className={styles.SocialLinkIcon} iconName={item.icon} />
                                            <span className="visuallyHidden">{item.link.label}</span>
                                        </Link>
                                    </li>
                                )}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.Copyright}>{`${copyright.year} © ${copyright.name}`}</div>
            </div>
        </footer>
    );
};
